import React, { useState } from "react";
import { FlexLeague, FlexMatch } from "../types/league";
import LeagueMatchComponent from "./LeagueMatch";
import {
  Trophy,
  Users,
  Calendar,
  Target,
  Award,
  TrendingUp,
  Clock,
  Star,
  Zap,
  Shield,
  Play,
  ArrowLeft,
  Settings,
  CheckCircle,
  XCircle,
  Plus,
  Filter,
} from "lucide-react";

interface FlexLeagueDashboardProps {
  league: FlexLeague;
  setLeague: (league: FlexLeague) => void;
  onReset: () => void;
}

const FlexLeagueDashboard: React.FC<FlexLeagueDashboardProps> = ({
  league,
  setLeague,
  onReset,
}) => {
  const [selectedMatch, setSelectedMatch] = useState<FlexMatch | null>(null);
  const [activeView, setActiveView] = useState<
    "standings" | "matches" | "stats" | "schedule"
  >("standings");
  const [showAddMatch, setShowAddMatch] = useState(false);

  const completedMatches = league.matches.filter((match) => match.isCompleted);
  const pendingMatches = league.matches.filter((match) => !match.isCompleted);
  const totalMatches = league.matches.length;

  const handleAddMatch = () => {
    setShowAddMatch(true);
  };

  const handleMatchClick = (match: FlexMatch | any) => {
    if (!match.isCompleted) {
      setSelectedMatch(match);
    }
  };

  const handleEnterScore = (
    matchId: string,
    team1Score: number,
    team2Score: number
  ) => {
    // Update match result
    const updatedMatches = league.matches.map((match) => {
      if (match.id === matchId) {
        return {
          ...match,
          result: {
            team1Score,
            team2Score,
            winner: team1Score > team2Score ? match.team1.id : match.team2.id,
            loser: team1Score > team2Score ? match.team2.id : match.team1.id,
            completed: true,
            isPerfectGame:
              (team1Score === 11 && team2Score === 0) ||
              (team2Score === 11 && team1Score === 0),
            isCloseMatch: Math.abs(team1Score - team2Score) <= 2,
          },
          isCompleted: true,
          completedDate: new Date(),
        };
      }
      return match;
    });

    // Update standings
    const updatedStandings = league.standings.map((standing) => {
      const teamMatches = updatedMatches.filter(
        (match) =>
          match.isCompleted &&
          (match.team1.id === standing.team.id ||
            match.team2.id === standing.team.id)
      );

      let wins = 0;
      let losses = 0;
      let points = 0;
      let gamesPlayed = 0;
      let gamesWon = 0;
      let gamesLost = 0;

      teamMatches.forEach((match) => {
        if (match.result) {
          gamesPlayed++;
          if (match.team1.id === standing.team.id) {
            gamesWon += match.result.team1Score;
            gamesLost += match.result.team2Score;
            if (match.result.winner === standing.team.id) {
              wins++;
              points += 3; // Win = 3 points
            } else {
              losses++;
              if (match.result.isCloseMatch) {
                points += 1; // Close loss = 1 point
              }
            }
          } else {
            gamesWon += match.result.team2Score;
            gamesLost += match.result.team1Score;
            if (match.result.winner === standing.team.id) {
              wins++;
              points += 3;
            } else {
              losses++;
              if (match.result.isCloseMatch) {
                points += 1;
              }
            }
          }
        }
      });

      return {
        ...standing,
        wins,
        losses,
        points,
        gamesPlayed,
        gamesWon,
        gamesLost,
        winPercentage: gamesPlayed > 0 ? (wins / gamesPlayed) * 100 : 0,
        pointsPerGame: gamesPlayed > 0 ? points / gamesPlayed : 0,
      };
    });

    // Sort standings by points, then win percentage
    updatedStandings.sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      return b.winPercentage - a.winPercentage;
    });

    const updatedLeague = {
      ...league,
      matches: updatedMatches,
      standings: updatedStandings,
      stats: {
        ...league.stats,
        totalMatches: updatedMatches.length,
        completedMatches: completedMatches.length + 1,
        averagePointsPerGame:
          updatedMatches.reduce(
            (total, match) =>
              total +
              (match.result?.team1Score || 0) +
              (match.result?.team2Score || 0),
            0
          ) /
          (completedMatches.length + 1) /
          2,
      },
      updatedAt: new Date(),
    };

    setLeague(updatedLeague);
    setSelectedMatch(null);
  };

  const getTeamProgress = (teamId: string) => {
    const teamMatches = league.matches.filter(
      (match) =>
        match.isCompleted &&
        (match.team1.id === teamId || match.team2.id === teamId)
    );
    const completed = teamMatches.length;
    const required = league.settings.requiredMatches;
    const percentage = (completed / required) * 100;
    return { completed, required, percentage };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={onReset}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Home
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {league.name}
                </h1>
                <p className="text-gray-600">Flex League</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-600">Period</p>
                <p className="font-semibold">
                  {league.settings.currentPeriod} of{" "}
                  {Math.ceil(league.settings.periodLength / 4)}
                </p>
              </div>
              <button
                onClick={handleAddMatch}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Match
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: "standings", name: "Standings", icon: Trophy },
              { id: "matches", name: "Matches", icon: Calendar },
              { id: "stats", name: "Statistics", icon: TrendingUp },
              { id: "schedule", name: "Schedule", icon: Clock },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveView(item.id as any)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    activeView === item.id
                      ? "border-purple-500 text-purple-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {item.name}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Standings View */}
        {activeView === "standings" && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  League Standings
                </h2>
                <p className="text-sm text-gray-600">
                  {completedMatches.length} of {totalMatches} matches completed
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Position
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Team
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Matches
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        W-L
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Points
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Win %
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Progress
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {league.standings.map((standing, index) => {
                      const progress = getTeamProgress(standing.team.id);
                      return (
                        <tr key={standing.team.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {index + 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {standing.team.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {standing.team.player1} &{" "}
                                {standing.team.player2}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {standing.gamesPlayed}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {standing.wins}-{standing.losses}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {standing.points}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {standing.winPercentage.toFixed(1)}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className="bg-purple-600 h-2 rounded-full"
                                  style={{ width: `${progress.percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-500">
                                {progress.completed}/{progress.required}
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Matches View */}
        {activeView === "matches" && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  All Matches
                </h2>
                <p className="text-sm text-gray-600">
                  {pendingMatches.length} pending, {completedMatches.length}{" "}
                  completed
                </p>
              </div>
              <div className="divide-y divide-gray-200">
                {league.matches.map((match) => (
                  <div key={match.id} className="px-6 py-4">
                    <LeagueMatchComponent
                      match={match}
                      onClick={handleMatchClick}
                      variant="compact"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Statistics View */}
        {activeView === "stats" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Calendar className="w-8 h-8 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      Total Matches
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {league.stats.totalMatches}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      Completed
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {league.stats.completedMatches}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TrendingUp className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      Avg Points/Game
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {league.stats.averagePointsPerGame.toFixed(1)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  League Statistics
                </h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">
                      Match Completion
                    </h4>
                    <div className="space-y-2">
                      {league.teams.map((team) => {
                        const progress = getTeamProgress(team.id);
                        return (
                          <div
                            key={team.id}
                            className="flex items-center justify-between"
                          >
                            <span className="text-sm text-gray-600">
                              {team.name}
                            </span>
                            <span className="text-sm font-medium">
                              {progress.completed}/{progress.required}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">
                      League Settings
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Required Matches:</span>
                        <span className="font-medium">
                          {league.settings.requiredMatches}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Period Length:</span>
                        <span className="font-medium">
                          {league.settings.periodLength} weeks
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Max Matches/Week:</span>
                        <span className="font-medium">
                          {league.settings.maxMatchesPerWeek}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Self Scheduling:</span>
                        <span className="font-medium">
                          {league.settings.allowSelfScheduling ? "Yes" : "No"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Schedule View */}
        {activeView === "schedule" && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  Match Schedule
                </h2>
                <p className="text-sm text-gray-600">
                  Self-scheduled matches with flexible timing
                </p>
              </div>
              <div className="p-6">
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium mb-2">
                    Flexible Scheduling
                  </p>
                  <p className="text-sm">
                    Teams can schedule matches at their convenience within the
                    league period. Use the "Add Match" button to schedule new
                    matches.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Score Entry Modal */}
      {selectedMatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Enter Match Score
              </h3>
              <button
                onClick={() => setSelectedMatch(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {selectedMatch.team1.name} Score
                </label>
                <input
                  type="number"
                  min="0"
                  max="13"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="0-13"
                  id="team1Score"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {selectedMatch.team2.name} Score
                </label>
                <input
                  type="number"
                  min="0"
                  max="13"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="0-13"
                  id="team2Score"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <div className="text-sm text-blue-800">
                  <strong>Pickleball Rules:</strong>
                  <ul className="mt-1 space-y-1">
                    <li>• First to 11 points wins</li>
                    <li>• Must win by 2 points</li>
                    <li>• Maximum score is 13 points</li>
                  </ul>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setSelectedMatch(null)}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const team1Score = parseInt(
                      (
                        document.getElementById(
                          "team1Score"
                        ) as HTMLInputElement
                      ).value
                    );
                    const team2Score = parseInt(
                      (
                        document.getElementById(
                          "team2Score"
                        ) as HTMLInputElement
                      ).value
                    );
                    if (
                      !isNaN(team1Score) &&
                      !isNaN(team2Score) &&
                      team1Score >= 0 &&
                      team1Score <= 13 &&
                      team2Score >= 0 &&
                      team2Score <= 13 &&
                      team1Score !== team2Score
                    ) {
                      handleEnterScore(
                        selectedMatch.id,
                        team1Score,
                        team2Score
                      );
                    }
                  }}
                  className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
                >
                  Submit Score
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlexLeagueDashboard;
